/* eslint-disable max-lines */
import * as React from "react";
import * as moment from "moment";
import { createValidator } from "not-valid";
import { Segment } from "semantic-ui-react";

import { QuestionAnswer } from "@teamprevent/common/questionnaires/model";
import { AccordionQuestionnaireSection } from "@teamprevent/common/questionnaires/components/AccordionQuestionnaireSection";
import { DateQuestion } from "@teamprevent/common/questionnaires/components/questions/DateQuestion";
import { DropdownQuestion } from "@teamprevent/common/questionnaires/components/questions/DropdownQuestion";
import { MultiButtonQuestion, YesOption } from "@teamprevent/common/questionnaires/components/questions/MultiButtonQuestion";
import { TextQuestion } from "@teamprevent/common/questionnaires/components/questions/TextQuestion";
import { getAnswerToQuestionWithId } from "@teamprevent/common/questionnaires/getAnswerToQuestionWithId";
import { TextSnippetSelector } from "@teamprevent/common/components/TextSnippetSelector";
import { AnsweredQuestion, AnsweredQuestionWithId } from "@teamprevent/common/model";

import {
    applyEqualityActQuestionOptions,
    Classification,
    classificationQuestionOptions,
    DetailsOfFurtherAppointments,
    detailsOfFurtherAppointmentsQuestionOptions,
    DetailsOfOnwardReferral,
    detailsOfOnwardReferralQuestionOptions,
    durationOfMedicalConditionQuestionOptions,
    durationOfPhasedReturnQuestionOptions,
    fitnessToWorkQuestionOptions,
    RiskAssessmentRecommendation,
    riskAssessmentRecommendationQuestionOptions,
} from "../models";

import "../styles/referral-clinical-notes-sections.scss";

export interface OutcomeAndOnwardManagementOfTheCaseSectionProps{
    questionnaireKey: string;
    questions: AnsweredQuestionWithId[];
    readonly: boolean;
    useSimplifiedNotesAndReport: boolean;
    isDateInTheFuture: (value: moment.Moment) => boolean;
    isDateInThePast: (value: moment.Moment) => boolean;
    warningTextHeader: () => React.ReactNode;
    updateSnippets: (snippetText: string, sectionId: string, id: string, questionText: string) => void;
    updateAnswer: (payload: { sectionId: string; id: string; question: AnsweredQuestion}) => void;
}

export const OutcomeAndOnwardManagementOfTheCaseSection: React.FunctionComponent<OutcomeAndOnwardManagementOfTheCaseSectionProps> = (
    {
        questionnaireKey,
        questions,
        readonly,
        useSimplifiedNotesAndReport,
        isDateInTheFuture,
        isDateInThePast,
        warningTextHeader,
        updateSnippets,
        updateAnswer
    }: OutcomeAndOnwardManagementOfTheCaseSectionProps) => {

    const outcomeAndOnwardManagementOfTheCaseSectionId = "OutcomeAndOnwardManagementOfTheCase";
    const commentOnReturnToWorkQuestion = {
        id: "OutcomeAndOnwardManagementOfTheCaseCommentOnReturnToWork",
        questionText: "Return to Work Plan (these comments will be added to your report)"
    };

    const chosenDetailsOfOnwardReferral = React.useMemo(() => {
        const detailsOfOnwardReferralAnswer = getAnswerToQuestionWithId(
            questions,
            "OutcomeAndOnwardManagementOfTheCaseDetailsOfOnwardReferral"
        );

        if (!detailsOfOnwardReferralAnswer) {
            return [];
        }

        if (Array.isArray(detailsOfOnwardReferralAnswer)) {
            return detailsOfOnwardReferralAnswer as string[];
        }

        if (typeof detailsOfOnwardReferralAnswer === "string") {
            return [detailsOfOnwardReferralAnswer];
        }

        return [];
    }, [questions]);

    const chosenDetailsOfFurtherAppointments = React.useMemo(() => {
        const detailsOfFurtherAppointments = getAnswerToQuestionWithId(
            questions,
            "OutcomeAndOnwardManagementOfTheCaseDetailsOfFurtherAppointments"
        );

        if (!detailsOfFurtherAppointments) {
            return [];
        }

        if (Array.isArray(detailsOfFurtherAppointments)) {
            return detailsOfFurtherAppointments as string[];
        }

        if (typeof detailsOfFurtherAppointments === "string") {
            return [detailsOfFurtherAppointments];
        }

        return [];
    }, [questions]);

    const chosenRiskAssessmentRecommendations = React.useMemo(() => {
        const riskAssessmentRecommendationsAnswer = getAnswerToQuestionWithId(
            questions,
            "OutcomeAndOnwardManagementOfTheCaseRiskAssessmentRecommendations"
        );

        if (!riskAssessmentRecommendationsAnswer) {
            return [];
        }

        if (Array.isArray(riskAssessmentRecommendationsAnswer)) {
            return riskAssessmentRecommendationsAnswer as string[];
        }

        if (typeof riskAssessmentRecommendationsAnswer === "string") {
            return [riskAssessmentRecommendationsAnswer];
        }

        return [];
    }, [questions]);

    const choosenOutcomeAndOnwardManagementOfTheCaseClassification = React.useMemo(() => getAnswerToQuestionWithId(
        questions,
        "OutcomeAndOnwardManagementOfTheCaseClassification"
    ), [questions]);

    const filteredDetailsOfOnwardReferralOptions = chosenDetailsOfOnwardReferral.some(d => d === DetailsOfOnwardReferral.None)
        ? detailsOfOnwardReferralQuestionOptions.filter(o => o.value === DetailsOfOnwardReferral.None)
        : detailsOfOnwardReferralQuestionOptions;

    const filteredDetailsOfFurtherAppointmentsQuestionOptions = chosenDetailsOfFurtherAppointments.some(d => d === DetailsOfFurtherAppointments.NoReview)
        ? detailsOfFurtherAppointmentsQuestionOptions.filter(o => o.value === DetailsOfFurtherAppointments.NoReview)
        : detailsOfFurtherAppointmentsQuestionOptions;

    const filteredRiskAssessmentRecommendationQuestionOptions = chosenRiskAssessmentRecommendations.some(d => d === RiskAssessmentRecommendation.None)
        ? riskAssessmentRecommendationQuestionOptions.filter(o => o.value === RiskAssessmentRecommendation.None)
        : riskAssessmentRecommendationQuestionOptions;

    const onChosenDetailsOfOnwardReferralChange = React.useCallback((answer: QuestionAnswer) => {
        const oldAnswer = getAnswerToQuestionWithId(
            questions,
            "OutcomeAndOnwardManagementOfTheCaseDetailsOfOnwardReferral"
        );

        if (oldAnswer !== answer
            && (answer === DetailsOfOnwardReferral.None || (Array.isArray(answer)
                && (answer as string[]).some((a => a === DetailsOfOnwardReferral.None))))) {
            updateAnswer({
                sectionId: outcomeAndOnwardManagementOfTheCaseSectionId,
                id: "OutcomeAndOnwardManagementOfTheCaseDetailsOfOnwardReferral",
                question: { question: "Details of any Onward Referral to Support Services being Made", answer: [DetailsOfOnwardReferral.None], isValid: true }
            });
        }
    }, [questions, updateAnswer]);

    const onChosenDetailsOfFurtherAppointments = React.useCallback((answer: QuestionAnswer) => {
        const oldAnswer = getAnswerToQuestionWithId(
            questions,
            "OutcomeAndOnwardManagementOfTheCaseDetailsOfFurtherAppointments"
        );

        if (oldAnswer !== answer
            && (answer === DetailsOfFurtherAppointments.NoReview || (Array.isArray(answer)
                && (answer as string[]).some((a => a === DetailsOfFurtherAppointments.NoReview))))) {
            updateAnswer({
                sectionId: outcomeAndOnwardManagementOfTheCaseSectionId,
                id: "OutcomeAndOnwardManagementOfTheCaseDetailsOfFurtherAppointments",
                question: {
                    question: "Details of any further appointments to be arranged with Occupational Health",
                    answer: [DetailsOfFurtherAppointments.NoReview],
                    isValid: true
                }
            });
        }
    }, [questions, updateAnswer]);

    const onChosenRiskAssessmentRecommendations = React.useCallback((answer: QuestionAnswer) => {
        const oldAnswer = getAnswerToQuestionWithId(
            questions,
            "OutcomeAndOnwardManagementOfTheCaseRiskAssessmentRecommendations"
        );

        if (oldAnswer !== answer
            && (answer === RiskAssessmentRecommendation.None || (Array.isArray(answer)
                && (answer as string[]).some((a => a === RiskAssessmentRecommendation.None))))) {
            updateAnswer({
                sectionId: outcomeAndOnwardManagementOfTheCaseSectionId,
                id: "OutcomeAndOnwardManagementOfTheCaseRiskAssessmentRecommendations",
                question: {
                    question: "Risk Assessment Recommendations",
                    answer: [RiskAssessmentRecommendation.None],
                    isValid: true
                }
            });
        }
    }, [questions, updateAnswer]);

    const isEmployeeIsUnlikelyToReturnForTheForeseeableFuture = React.useMemo(() => {
        const answer = getAnswerToQuestionWithId(
            questions,
            "OutcomeAndOnwardManagementOfTheCaseEmployeeUnlikelyToReturn"
        );
        return answer === YesOption;
    }, [questions]);

    const updateSnippetsOnSelect = React.useCallback((
        sectionId: string,
        id: string,
        questionText: string) => (selectedSnippet: string) => {
        updateSnippets(
            selectedSnippet,
            sectionId,
            id,
            questionText
        );
    } , [updateSnippets]);

    return (
        <>
            <AccordionQuestionnaireSection
                title="Outcome and Onward Management of the Case"
                key={outcomeAndOnwardManagementOfTheCaseSectionId}
                id={outcomeAndOnwardManagementOfTheCaseSectionId}
                index={14}
            >
                {warningTextHeader()}
                <>
                    <p className="clinical-notes-header">Classification</p>
                    <DropdownQuestion
                        hideDivider
                        inline
                        readonly={readonly}
                        questionConfig={({
                            id: "OutcomeAndOnwardManagementOfTheCaseClassification",
                            questionText: "Classification (These comments will be added to your report)"
                        })}
                        questionClassName={"question-red"}
                        options={classificationQuestionOptions}
                        mandatory
                    />
                    {(choosenOutcomeAndOnwardManagementOfTheCaseClassification === Classification.NotAtWorkMedicalCondition
                        || choosenOutcomeAndOnwardManagementOfTheCaseClassification === Classification.NotAtWorkManagementIssue) ? (
                            <DateQuestion
                                hideDivider
                                inline
                                readonly={readonly}
                                questionConfig={({
                                    id: "OutcomeAndOnwardManagementOfTheCaseFirstDayOfAbsence",
                                    questionText: "First Day of Absence (these comments will be added to your report)"
                                })}
                                questionClassName={"question-red"}
                                validators={[
                                    createValidator((value: moment.Moment) =>
                                        isDateInThePast(value), "This date must be in the past")
                                ]}
                                mandatory
                            />
                        ) : ("")}
                </>
                {useSimplifiedNotesAndReport
                    ? ("")
                    : (
                        <>
                            <p className="clinical-notes-header additional-padding">Equality Act</p>
                            <TextQuestion
                                hideDivider
                                inline
                                readonly={readonly}
                                questionConfig={({
                                    id: "OutcomeAndOnwardManagementOfTheCaseDescriptionOfThePhysicalOrMentalImpairment",
                                    questionText: "Description of the Physical or Mental Impairment (these comments will be added to your report)"
                                })}
                                questionClassName={"question-red"}
                                multiline
                                mandatory
                                textSnippetSelector={
                                    <TextSnippetSelector
                                        padded
                                        onSelect={updateSnippetsOnSelect(
                                            outcomeAndOnwardManagementOfTheCaseSectionId,
                                            "OutcomeAndOnwardManagementOfTheCaseDescriptionOfThePhysicalOrMentalImpairment",
                                            "Description of the Physical or Mental Impairment (these comments will be added to your report)"
                                        )}
                                    />
                                }
                            />
                            <DropdownQuestion
                                hideDivider
                                inline
                                readonly={readonly}
                                questionConfig={({
                                    id: "OutcomeAndOnwardManagementOfTheCaseDurationOfMedicalCondition",
                                    questionText: "Duration of Medical Condition (these comments will be added to your report)"
                                })}
                                questionClassName={"question-red"}
                                options={durationOfMedicalConditionQuestionOptions}
                                mandatory
                            />
                            <TextQuestion
                                hideDivider
                                inline
                                readonly={readonly}
                                questionConfig={({
                                    id: "OutcomeAndOnwardManagementOfTheCaseEffectOfMedicalCondition",
                                    questionText: "Effect of Medical Condition on Normal Day to Day Activities (these comments will be added to"
                            + " your report)"
                                })}
                                multiline
                                questionClassName={"question-red"}
                                mandatory
                                textSnippetSelector={
                                    <TextSnippetSelector
                                        padded
                                        onSelect={updateSnippetsOnSelect(
                                            outcomeAndOnwardManagementOfTheCaseSectionId,
                                            "OutcomeAndOnwardManagementOfTheCaseEffectOfMedicalCondition",
                                            "Effect of Medical Condition on Normal Day to Day Activities (these comments will be added to your "
                                + "report)"
                                        )}
                                    />
                                }
                            />
                            <DropdownQuestion
                                hideDivider
                                inline
                                readonly={readonly}
                                questionConfig={({
                                    id: "OutcomeAndOnwardManagementOfTheCaseApplyEqualityAct",
                                    questionText: "Is the Equality Act Likely to Apply? Please note this is in the opinion of the Occupational "
                            + "Health Clinician but ultimately the decision is a legal one "
                            + "(these comments will be added to your report)"
                                })}
                                options={applyEqualityActQuestionOptions}
                                questionClassName={"question-red"}
                                mandatory
                            />
                        </>
                    )
                }
                <>
                    <p className="clinical-notes-header additional-padding">Fitness to Work</p>
                    <DropdownQuestion
                        hideDivider
                        inline
                        readonly={readonly}
                        questionConfig={({
                            id: "OutcomeAndOnwardManagementOfTheCaseFitnessToWork",
                            questionText: "Fitness to Work (these comments will be added to your report)"
                        })}
                        questionClassName={"question-red"}
                        options={fitnessToWorkQuestionOptions}
                        mandatory
                    />
                </>
                {(
                    choosenOutcomeAndOnwardManagementOfTheCaseClassification === Classification.NotAtWorkManagementIssue ||
                    choosenOutcomeAndOnwardManagementOfTheCaseClassification === Classification.NotAtWorkMedicalCondition
                ) &&
                    <>
                        <p className="clinical-notes-header additional-padding">Return to Work</p>
                        {!isEmployeeIsUnlikelyToReturnForTheForeseeableFuture &&
                            <DateQuestion
                                hideDivider
                                inline
                                readonly={readonly}
                                questionConfig={({
                                    id: "OutcomeAndOnwardManagementOfTheCaseEstimatedReturnToWorkDate",
                                    questionText: "Estimated Return to Work Date"
                                })}
                                questionClassName={"question-red"}
                                validators={[createValidator((value: moment.Moment) =>
                                    isDateInTheFuture(value),
                                "Should be today or in the future")]}
                                mandatory
                            />
                        }
                        {!(readonly && !isEmployeeIsUnlikelyToReturnForTheForeseeableFuture) &&
                            <MultiButtonQuestion
                                hideDivider
                                inline
                                readonly={readonly}
                                questionConfig={({
                                    id: "OutcomeAndOnwardManagementOfTheCaseEmployeeUnlikelyToReturn",
                                    questionText: "Is employee unlikely to return for the foreseeable future"
                                })}
                                questionClassName={"question-red"}
                                mandatory
                            />
                        }
                        {isEmployeeIsUnlikelyToReturnForTheForeseeableFuture &&
                            <TextQuestion
                                inline
                                readonly={readonly}
                                questionConfig={({
                                    id: "OutcomeAndOnwardManagementOfTheCaseEmployeeUnlikelyToReturnFurtherDetails",
                                    questionText: "Further details (these comments will be added to your report)"
                                })}
                                mandatory
                                multiline
                                questionClassName={"question-red"}
                                textSnippetSelector={
                                    <TextSnippetSelector
                                        padded
                                        onSelect={updateSnippetsOnSelect(
                                            outcomeAndOnwardManagementOfTheCaseSectionId,
                                            "OutcomeAndOnwardManagementOfTheCaseEmployeeUnlikelyToReturnFurtherDetails",
                                            "Further details (these comments will be added to your report)"
                                        )}
                                    />
                                }
                            />
                        }
                        <MultiButtonQuestion
                            hideDivider
                            inline
                            readonly={readonly}
                            questionConfig={({
                                id: "OutcomeAndOnwardManagementOfTheCasePhasedReturn",
                                questionText: "Is a Phased Return to Work Required? (these comments will be added to your report)"
                            })}
                            questionClassName={"question-red"}
                            mandatory
                        />
                        {getAnswerToQuestionWithId(
                            questions,
                            "OutcomeAndOnwardManagementOfTheCasePhasedReturn") === YesOption &&
                            <DropdownQuestion
                                hideDivider
                                inline
                                readonly={readonly}
                                questionConfig={({
                                    id: "OutcomeAndOnwardManagementOfTheCaseDurationOfPhasedReturn",
                                    questionText: "Duration of Phased Return (these comments will be added to your report)"
                                })}
                                questionClassName={"question-red"}
                                options={durationOfPhasedReturnQuestionOptions}
                                mandatory
                            />
                        }
                    </>
                }
                {!readonly &&
                    <Segment textAlign="left" basic compact>

                    </Segment>
                }
                <TextQuestion
                    hideDivider
                    inline
                    readonly={readonly}
                    questionConfig={commentOnReturnToWorkQuestion}
                    questionClassName={"question-red"}
                    multiline
                    textSnippetSelector={
                        <TextSnippetSelector
                            padded
                            onSelect={updateSnippetsOnSelect(
                                outcomeAndOnwardManagementOfTheCaseSectionId,
                                commentOnReturnToWorkQuestion.id,
                                commentOnReturnToWorkQuestion.questionText
                            )}
                        />
                    }
                />
                <p className="clinical-notes-header additional-padding">Further Interventions / Sign Posting</p>
                <DropdownQuestion
                    hideDivider
                    inline
                    readonly={readonly}
                    questionConfig={({
                        id: "OutcomeAndOnwardManagementOfTheCaseDetailsOfOnwardReferral",
                        questionText: "Details of any Onward Referral to Support Services being Made (these comments will be added to your report)"
                    })}
                    questionClassName={"question-red"}
                    onAnswerChange={onChosenDetailsOfOnwardReferralChange}
                    options={filteredDetailsOfOnwardReferralOptions}
                    mandatory
                    multiple
                />
                {
                    chosenDetailsOfOnwardReferral.some(d => d === DetailsOfOnwardReferral.OtherDetailsBelow) &&
                    <>
                        <TextQuestion
                            hideDivider
                            inline
                            readonly={readonly}
                            questionConfig={({
                                id: "OutcomeAndOnwardManagementOfTheCaseDetailsOfOnwardReferralOther",
                                questionText: "Details (these comments will be added to your report)"
                            })}
                            questionClassName={"question-red"}
                            multiline
                            mandatory
                            textSnippetSelector={
                                <TextSnippetSelector
                                    padded
                                    onSelect={updateSnippetsOnSelect(
                                        outcomeAndOnwardManagementOfTheCaseSectionId,
                                        "OutcomeAndOnwardManagementOfTheCaseDetailsOfOnwardReferralOther",
                                        "Details (these comments will be added to your report)"
                                    )}
                                />
                            }
                        />
                    </>
                }
                <DropdownQuestion
                    hideDivider
                    inline
                    readonly={readonly}
                    questionConfig={({
                        id: "OutcomeAndOnwardManagementOfTheCaseDetailsOfFurtherAppointments",
                        questionText: "Details of any further appointments to be arranged with Occupational Health (these comments will be added"
                            + " to your report)"
                    })}
                    questionClassName={"question-red"}
                    onAnswerChange={onChosenDetailsOfFurtherAppointments}
                    options={filteredDetailsOfFurtherAppointmentsQuestionOptions}
                    mandatory
                    multiple
                />
                <MultiButtonQuestion
                    hideDivider
                    inline
                    readonly={readonly}
                    questionConfig={({
                        id: "OutcomeAndOnwardManagementOfTheCaseFurtherMedicalInformationBeingRequested",
                        questionText: "Further Medical Information being requested (these comments will be added to your report)"
                    })}
                    questionClassName={"question-red"}
                    mandatory
                />
                {getAnswerToQuestionWithId(
                    questions,
                    "OutcomeAndOnwardManagementOfTheCaseFurtherMedicalInformationBeingRequested") === YesOption &&
                    <>
                        <TextQuestion
                            hideDivider
                            inline
                            readonly={readonly}
                            questionConfig={({
                                id: "OutcomeAndOnwardManagementOfTheCaseFurtherMedicalInformationBeingRequestedDetails",
                                questionText: "Comments (these comments will be added to your report)"
                            })}
                            questionClassName={"question-red"}
                            multiline
                            mandatory
                            textSnippetSelector={
                                <TextSnippetSelector
                                    padded
                                    onSelect={updateSnippetsOnSelect(
                                        outcomeAndOnwardManagementOfTheCaseSectionId,
                                        "OutcomeAndOnwardManagementOfTheCaseFurtherMedicalInformationBeingRequestedDetails",
                                        "Comments (these comments will be added to your report)"
                                    )}
                                />
                            }
                        />
                    </>
                }
                <p className="clinical-notes-header additional-padding">Risk Assessment Recommendations</p>
                <DropdownQuestion
                    hideDivider
                    inline
                    readonly={readonly}
                    questionConfig={({
                        id: "OutcomeAndOnwardManagementOfTheCaseRiskAssessmentRecommendations",
                        questionText: "Risk Assessment Recommendations (these comments will be added to your report)"
                    })}
                    questionClassName={"question-red"}
                    onAnswerChange={onChosenRiskAssessmentRecommendations}
                    options={filteredRiskAssessmentRecommendationQuestionOptions}
                    mandatory
                    multiple
                />
                {chosenRiskAssessmentRecommendations.some(d => d === RiskAssessmentRecommendation.OtherPleaseProvideDetails) &&
                    <>
                        <TextQuestion
                            hideDivider
                            inline
                            readonly={readonly}
                            questionConfig={({
                                id: "OutcomeAndOnwardManagementOfTheCaseRiskAssessmentRecommendationsOther",
                                questionText: "Details (these comments will be added to your report)"
                            })}
                            questionClassName={"question-red"}
                            multiline
                            mandatory
                            textSnippetSelector={
                                <TextSnippetSelector
                                    padded
                                    onSelect={updateSnippetsOnSelect(
                                        outcomeAndOnwardManagementOfTheCaseSectionId,
                                        "OutcomeAndOnwardManagementOfTheCaseRiskAssessmentRecommendationsOther",
                                        "Details (these comments will be added to your report)"
                                    )}
                                />
                            }
                        />
                    </>
                }
                {!readonly && <Segment textAlign="left" basic compact></Segment>}
                <TextQuestion
                    hideDivider
                    inline
                    readonly={readonly}
                    questionConfig={({
                        id: "OutcomeAndOnwardManagementOfTheCaseSummaryAnyOtherComments",
                        questionText: "Summary / Any other comments"
                    })}
                    multiline
                    outdated={{
                        isOutdated: false,
                        questionnaireKey,
                        sectionId: outcomeAndOnwardManagementOfTheCaseSectionId
                    }}
                    textSnippetSelector={
                        <TextSnippetSelector
                            padded
                            onSelect={updateSnippetsOnSelect(
                                outcomeAndOnwardManagementOfTheCaseSectionId,
                                "OutcomeAndOnwardManagementOfTheCaseSummaryAnyOtherComments",
                                "Summary / Any other comments"
                            )}
                        />
                    }
                />
                <div className={"fields-divider"} />
            </AccordionQuestionnaireSection>
        </>
    );
};
